/**
 * 客户端健康评分
 * 综合 CPU/内存/延迟/丢包计算分数
 */
export interface HealthMetrics {
  cpuPercent: number
  memoryPercent: number
  latencyMs: number
  packetLossPercent: number
  diskPercent?: number
  uptimeSeconds?: number
}

export interface HealthScoreWeights {
  cpu?: number
  memory?: number
  latency?: number
  packetLoss?: number
  disk?: number
}

export type HealthLevel = 'healthy' | 'warning' | 'critical'

export class HealthScore {
  private weights: Required<HealthScoreWeights>
  private history: Array<{ score: number; timestamp: number }> = []
  private maxHistory: number

  constructor(weights?: HealthScoreWeights, maxHistory: number = 100) {
    this.weights = {
      cpu: weights?.cpu ?? 0.3,
      memory: weights?.memory ?? 0.25,
      latency: weights?.latency ?? 0.25,
      packetLoss: weights?.packetLoss ?? 0.15,
      disk: weights?.disk ?? 0.05,
    }
    this.maxHistory = maxHistory
  }

  /** 计算健康评分 (0-100) */
  calculate(metrics: HealthMetrics): number {
    const cpuScore = Math.max(0, 100 - metrics.cpuPercent)
    const memScore = Math.max(0, 100 - metrics.memoryPercent)
    const latScore = Math.max(0, 100 - Math.min(metrics.latencyMs / 5, 100))
    const lossScore = Math.max(0, 100 - metrics.packetLossPercent * 5)
    const diskScore = metrics.diskPercent !== undefined
      ? Math.max(0, 100 - metrics.diskPercent)
      : 100

    const score = Math.round(
      cpuScore * this.weights.cpu +
      memScore * this.weights.memory +
      latScore * this.weights.latency +
      lossScore * this.weights.packetLoss +
      diskScore * this.weights.disk,
    )

    this.history.push({ score, timestamp: Date.now() })
    if (this.history.length > this.maxHistory) this.history.shift()

    return score
  }

  /** 获取健康等级 */
  getLevel(score: number): HealthLevel {
    if (score >= 70) return 'healthy'
    if (score >= 40) return 'warning'
    return 'critical'
  }

  /** 获取趋势（最近N个评分的平均） */
  getTrend(count: number = 10): number {
    const recent = this.history.slice(-count)
    if (recent.length === 0) return 0
    return Math.round(recent.reduce((sum, h) => sum + h.score, 0) / recent.length)
  }

  /** 获取历史 */
  getHistory(): typeof this.history {
    return [...this.history]
  }

  /** 重置 */
  reset(): void {
    this.history = []
  }
}
